import * as React from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSearchBox } from 'react-instantsearch'
import debounce from 'lodash.debounce'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { SearchIcon } from 'primitives/icons'
import { facebookEvent, FacebookEventName } from '@repo/analytics/facebook'
import { useTranslations } from 'next-intl'
import { useSiteData } from '@repo/web-ui/hooks/use-site-data'

export function SearchInput() {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const t = useTranslations('pages.search')
  const { refine, query } = useSearchBox()

  const { data: siteData } = useSiteData()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const isBannerDisplayed =
    siteData?.allAccessYearlyBanner ||
    siteData?.allAccessYearlyTrialBanner ||
    siteData?.allAccessYearlyExtendBanner

  function sendEvent() {
    let bannerDisplayed = false

    if (isBannerDisplayed) {
      bannerDisplayed = true
    }

    sendAmplitudeEvent(AmplitudeEvents.PRODUCTS_SEARCHED, {
      query: inputRef.current?.value ?? '',
      source: 'search-page',
      banner_above_nav: bannerDisplayed ? isBannerDisplayed?.bannerAboveNav || '' : '',
      banner_above_nav_state: bannerDisplayed ? isBannerDisplayed?.bannerAboveNavState || '' : '',
    })

    facebookEvent(FacebookEventName.SEARCH, {
      search_string: inputRef.current?.value ?? '',
    })
  }

  const handleSearch = React.useCallback(function handleSearch() {
    refine(inputRef.current?.value ?? '')
    sendEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      refine(inputRef.current?.value ?? '')
    }
  }

  // TODO check if this is still needed
  React.useEffect(() => {
    let oldQuery = searchParams.get('query') || ''
    const handleRouteChange = (url: string) => {
      const urlParams = new URLSearchParams(new URL(url, window.location.origin).search)
      const newQuery = urlParams.get('query') || ''

      if (oldQuery !== newQuery) {
        inputRef.current?.focus()
        oldQuery = newQuery
      }
    }

    handleRouteChange(`${pathname}?${searchParams.toString()}`)
  }, [pathname, searchParams])

  const debouncedCallback = React.useMemo(() => {
    return debounce(handleSearch, 1000)
  }, [handleSearch])

  function handleChange() {
    debouncedCallback()
  }

  return (
    <div className="relative flex w-full flex-col">
      <div className="relative flex w-full items-center">
        <input
          data-testid="search-input-sidebar"
          ref={inputRef}
          onChange={handleChange}
          className="placeholder-transition h-[40px] w-full rounded-[6px] border border-gray-700 bg-gray-900 px-5 py-[10px] pr-10 text-gray-50 placeholder:text-gray-700 focus:outline-none"
          type="text"
          placeholder={t('placeholder')}
          onKeyPress={handleKeyPress}
          defaultValue={query}
        />

        <button
          type="button"
          onClick={handleSearch}
          className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-gray-200"
        >
          <SearchIcon aria-label="search" className="size-4" />
        </button>
      </div>
    </div>
  )
}
